import React, { useEffect, useState } from "react";
import { api, formatApiError, rupiah } from "@/lib/api";
import { PageHeader, Empty } from "@/components/common";
import { usePaged, Pager } from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Coins, Search, Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import { ReceiptDialog } from "@/components/ReceiptDialog";

export default function KeuKebutuhan() {
  const [students, setStudents] = useState([]);
  const [query, setQuery] = useState("");
  const [target, setTarget] = useState(null);
  const [direction, setDirection] = useState("add");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [receipt, setReceipt] = useState(null);

  const load = () => api.get("/admin/personal-balances").then((r) => setStudents(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const open = (s, dir) => { setTarget(s); setDirection(dir); setAmount(""); setNote(""); };

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Jumlah tidak valid");
    try {
      const { data } = await api.post(`/admin/students/${target.id}/balance`, { amount: amt, direction, note });
      toast.success(direction === "add" ? "Uang jajan ditambahkan" : "Uang jajan ditarik");
      setReceipt({ tx_id: data.tx_id, title: direction === "add" ? "Setoran Tunai Berhasil" : "Penarikan Tunai Berhasil",
        student: target.name, jenis: `${direction === "add" ? "Setoran" : "Penarikan"} Uang Jajan`, amount: amt, balance_after: data.personal_balance });
      setTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const filtered = students.filter((s) =>
    s.name.toLowerCase().includes(query.toLowerCase()) || (s.nisn || "").includes(query));
  const pg = usePaged(filtered, 10);

  return (
    <div>
      <PageHeader
        title="Uang Jajan"
        subtitle="Uang jajan santri untuk belanja di Kopontren. Bisa ditambah/ditarik tunai oleh admin; orang tua juga bisa top-up via bank."
      />

      <div className="relative mb-4 max-w-md">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="kebutuhan-search" placeholder="Cari santri / NISN…" className="pl-10 rounded-xl py-6 bg-white" />
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-x-auto">
        {filtered.length === 0 ? (
          <Empty><Coins className="mx-auto mb-2" /> Tidak ada santri.</Empty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Nama</TableHead>
                <TableHead>NISN</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead className="text-right">Uang Jajan</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pg.pageRows.map((s) => (
                <TableRow key={s.id} data-testid={`kebutuhan-row-${s.id}`}>
                  <TableCell className="font-medium text-stone-800">{s.name}</TableCell>
                  <TableCell className="font-mono text-sm">{s.nisn}</TableCell>
                  <TableCell><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${s.gender === "putra" ? "bg-blue-100 text-blue-700" : "bg-pink-100 text-pink-700"}`}>{s.gender}</span></TableCell>
                  <TableCell className="text-right font-mono font-semibold text-emerald-700">{rupiah(s.personal_balance)}</TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-2">
                      <Button size="sm" onClick={() => open(s, "add")} data-testid={`add-saldo-${s.id}`} className="bg-emerald-600 hover:bg-emerald-700 rounded-lg">
                        <Plus size={14} className="mr-1" /> Tambah
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => open(s, "withdraw")} data-testid={`withdraw-saldo-${s.id}`} className="rounded-lg">
                        <Minus size={14} className="mr-1" /> Tarik
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Pager {...pg} />
      </div>

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {direction === "add" ? "Tambah Uang Jajan (Tunai)" : "Tarik Uang Jajan"} — {target?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="rounded-xl bg-stone-50 border p-3 text-sm text-stone-600">
              Uang jajan saat ini: <b className="text-emerald-700">{rupiah(target?.personal_balance || 0)}</b>
            </div>
            <div className="space-y-2">
              <Label>Jumlah (Rp)</Label>
              <Input type="number" data-testid="saldo-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label>Catatan (opsional)</Label>
              <Input data-testid="saldo-note-input" value={note} onChange={(e) => setNote(e.target.value)} className="rounded-xl" placeholder="cth: setor tunai di kantor" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submit} data-testid="confirm-saldo-button" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">
              {direction === "add" ? "Tambah" : "Tarik"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ReceiptDialog receipt={receipt} onClose={() => setReceipt(null)} />
    </div>
  );
}
